import React, { useState, useEffect } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { ReactComponent as SelectBlock } from "../../../../icons/Select_block.svg";

interface DateRangeSelectorProps {
  placeholder?: string;
  startDate?: Date | string | null;
  endDate?: Date | string | null;
  onDateChange?: (
    startDate: Date | null,
    endDate: Date | null,
    allDates: Array<{ value: string; label: string; isFixed: boolean }>
  ) => void;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  placeholder = "Select Date Range",
  startDate = null,
  endDate = null,
  onDateChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [internalStartDate, setInternalStartDate] = useState<Date | null>(null);
  const [internalEndDate, setInternalEndDate] = useState<Date | null>(null);
  const [submittedStartDate, setSubmittedStartDate] = useState<Date | null>(
    null
  );
  const [submittedEndDate, setSubmittedEndDate] = useState<Date | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingEnd, setSelectingEnd] = useState(false);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  const parseDate = (date: Date | string | null): Date | null => {
    if (!date) return null;
    if (date instanceof Date) return date;

    const parts = date.split("-");
    if (parts.length === 3) {
      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1;
      const day = parseInt(parts[2]);
      return new Date(year, month, day);
    }

    return null;
  };

  // Initialize and sync with props
  useEffect(() => {
    const parsedStart = parseDate(startDate);
    const parsedEnd = parseDate(endDate);
    // Update calendar view to show the start date range
    if (parsedStart) {
      const newMonth = new Date(
        parsedStart.getFullYear(),
        parsedStart.getMonth(),
        1
      );
      setCurrentMonth(newMonth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    const day = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return day === 0 ? 6 : day - 1;
  };

  const formatDate = (date: Date | null) => {
    if (!date) return "";
    const month = monthNames[date.getMonth()];
    return `${date.getDate()} ${month.slice(0, 3)}, ${date.getFullYear()}`;
  };

  const formatDateToString = (date: Date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const getAllDatesBetween = (start: Date, end: Date) => {
    const dates: Array<{ value: string; label: string; isFixed: boolean }> = [];
    const currentDate = new Date(start);

    while (currentDate <= end) {
      const dateString = formatDateToString(currentDate);
      dates.push({
        value: dateString,
        label: dateString,
        isFixed: false,
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  };

  const formatDisplayText = () => {
    if (
      submittedStartDate &&
      submittedEndDate &&
      submittedStartDate.getTime() !== submittedEndDate.getTime()
    ) {
      return `${formatDate(submittedStartDate)} - ${formatDate(
        submittedEndDate
      )}`;
    } else if (submittedStartDate) {
      return formatDate(submittedStartDate);
    }
    return placeholder;
  };

  const isDateDisabled = (day: number, monthOffset: number) => {
    const propStartDate = parseDate(startDate);
    const propEndDate = parseDate(endDate);

    if (propStartDate && propEndDate) {
      const date = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() + monthOffset,
        day
      );

      date.setHours(0, 0, 0, 0);
      const start = new Date(propStartDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(propEndDate);
      end.setHours(0, 0, 0, 0);

      return date < start || date > end;
    }

    return false;
  };

  const handleDateClick = (day: number, monthOffset: number) => {
    if (isDateDisabled(day, monthOffset)) {
      return;
    }

    const selectedDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + monthOffset,
      day
    );

    // If selecting end date, finalize the range
    if (internalStartDate && selectingEnd) {
      // Check if clicking the same start date while selecting end
      if (selectedDate.toDateString() === internalStartDate.toDateString()) {
        // Deselect everything
        setInternalStartDate(null);
        setInternalEndDate(null);
        setSelectingEnd(false);
        setHoverDate(null);
        return;
      }

      if (selectedDate < internalStartDate) {
        setInternalEndDate(internalStartDate);
        setInternalStartDate(selectedDate);
      } else {
        setInternalEndDate(selectedDate);
      }
      setSelectingEnd(false);
      setHoverDate(null);
    }
    // If no start date or starting a new selection
    else if (!internalStartDate) {
      setInternalStartDate(selectedDate);
      setInternalEndDate(selectedDate);
      setSelectingEnd(true);
      setHoverDate(null);
    }
    // If both dates are already selected (complete range), start new selection
    else if (internalStartDate && internalEndDate && !selectingEnd) {
      setInternalStartDate(selectedDate);
      setInternalEndDate(selectedDate);
      setSelectingEnd(true);
      setHoverDate(null);
    }
  };

  const handleDateHover = (day: number, monthOffset: number) => {
    if (isDateDisabled(day, monthOffset)) {
      return;
    }

    if (internalStartDate && selectingEnd) {
      const hoveredDate = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() + monthOffset,
        day
      );
      setHoverDate(hoveredDate);
    }
  };

  const isDateSelected = (day: number, monthOffset: number) => {
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + monthOffset,
      day
    );

    // If hovering while selecting end date, show preview
    if (internalStartDate && selectingEnd && hoverDate) {
      const previewStart =
        hoverDate < internalStartDate ? hoverDate : internalStartDate;
      const previewEnd =
        hoverDate > internalStartDate ? hoverDate : internalStartDate;
      return date >= previewStart && date <= previewEnd;
    }

    if (internalStartDate && internalEndDate) {
      return date >= internalStartDate && date <= internalEndDate;
    }

    return (
      internalStartDate &&
      date.toDateString() === internalStartDate.toDateString()
    );
  };

  const isStartOrEndDate = (day: number, monthOffset: number) => {
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + monthOffset,
      day
    );

    // If hovering while selecting, highlight hover position
    if (internalStartDate && selectingEnd && hoverDate) {
      return (
        date.toDateString() === internalStartDate.toDateString() ||
        date.toDateString() === hoverDate.toDateString()
      );
    }

    return (
      (internalStartDate &&
        date.toDateString() === internalStartDate.toDateString()) ||
      (internalEndDate &&
        date.toDateString() === internalEndDate.toDateString())
    );
  };

  const renderCalendar = (monthOffset: number) => {
    const displayMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + monthOffset,
      1
    );
    const daysInMonth = getDaysInMonth(displayMonth);
    const firstDay = getFirstDayOfMonth(displayMonth);
    const prevMonthDays = getDaysInMonth(
      new Date(displayMonth.getFullYear(), displayMonth.getMonth() - 1, 1)
    );

    const days = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      const day = prevMonthDays - i;
      days.push(
        <div
          key={`prev-${i}`}
          className="text-gray-300 text-center py-1 text-xs"
        >
          {day}
        </div>
      );
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const selected = isDateSelected(day, monthOffset);
      const isEdge = isStartOrEndDate(day, monthOffset);
      const disabled = isDateDisabled(day, monthOffset);

      days.push(
        <div
          key={day}
          onClick={() => handleDateClick(day, monthOffset)}
          onMouseEnter={() => handleDateHover(day, monthOffset)}
          onMouseLeave={() => selectingEnd && setHoverDate(null)}
          className={`text-center py-1 text-xs rounded transition-colors ${disabled
            ? "text-gray-300 cursor-not-allowed"
            : selected
              ? isEdge
                ? "bg-[#F1935C] text-white font-semibold cursor-pointer"
                : "bg-[#ffe4cd] text-[#F1935C] cursor-pointer"
              : "hover:bg-gray-100 cursor-pointer"
            }`}
        >
          {day}
        </div>
      );
    }

    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      days.push(
        <div
          key={`next-${day}`}
          className="text-gray-300 text-center py-1 text-xs"
        >
          {day}
        </div>
      );
    }

    return (
      <div className="flex-1">
        <div className="text-center font-medium mb-2 text-gray-500 text-sm">
          {monthNames[displayMonth.getMonth()]} {displayMonth.getFullYear()}
        </div>
        <div className="grid grid-cols-7 gap-1 mb-1">
          {daysOfWeek.map((day) => (
            <div
              key={day}
              className="text-center text-xs font-medium text-gray-400 py-1"
            >
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">{days}</div>
      </div>
    );
  };

  const handlePrevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  const handleNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  const handleSubmit = () => {
    if (onDateChange) {
      if (internalStartDate && internalEndDate) {
        if (internalStartDate.getTime() === internalEndDate.getTime()) {
          // Single date selected
          const singleDate = [
            {
              value: formatDateToString(internalStartDate),
              label: formatDateToString(internalStartDate),
              isFixed: false,
            },
          ];
          onDateChange(internalStartDate, internalStartDate, singleDate);
        } else {
          // Date range selected
          const allDates = getAllDatesBetween(
            internalStartDate,
            internalEndDate
          );
          onDateChange(internalStartDate, internalEndDate, allDates);
        }
      } else {
        // No dates selected - clear the selection with empty array
        onDateChange(null, null, []);
      }
    }
    setSubmittedStartDate(internalStartDate);
    setSubmittedEndDate(internalEndDate);
    setIsOpen(false);
  };

  const handleClear = () => {
    if (onDateChange) {
      onDateChange(null, null, []);
    }
    setInternalStartDate(null);
    setInternalEndDate(null);
    setSubmittedStartDate(null);
    setSubmittedEndDate(null);
    setSelectingEnd(false);
    setHoverDate(null);
    setIsOpen(false);
  };

  const handleOpen = () => {
    // Load submitted dates when opening
    setInternalStartDate(submittedStartDate);
    setInternalEndDate(submittedEndDate);
    setSelectingEnd(false);
    setHoverDate(null);

    // Set calendar to show based on props or submitted dates
    const parsedStart = parseDate(startDate);
    if (parsedStart) {
      setCurrentMonth(
        new Date(parsedStart.getFullYear(), parsedStart.getMonth(), 1)
      );
    } else if (submittedStartDate) {
      setCurrentMonth(
        new Date(
          submittedStartDate.getFullYear(),
          submittedStartDate.getMonth(),
          1
        )
      );
    } else {
      setCurrentMonth(new Date());
    }

    setIsOpen(true);
  };

  const handleClose = () => {
    // Reset internal dates to submitted dates when closing without submit
    setInternalStartDate(submittedStartDate);
    setInternalEndDate(submittedEndDate);
    setSelectingEnd(false);
    setHoverDate(null);
    setIsOpen(false);
  };

  return (
    <div className="inline-block">
      <button
        onClick={handleOpen}
        className="flex items-center gap-2 p-2 border border-gray-300 rounded-lg hover:border-gray-400 transition-colors bg-white justify-between"
      >
        <SelectBlock className="w-[18px] h-[18px] cursor-pointer" />
        <span className="text-gray-500 text-sm font-medium">
          {formatDisplayText()}
        </span>
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl mx-4">
            <div className="flex items-center justify-between p-2 border-b">
              <h2 className="text-sm font-medium text-[#34656d]">
                Select Date Range
              </h2>
              <button
                onClick={handleClose}
                className="text-[#34656d] hover:text-[#34656d]"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex">
              <div className="flex-1 p-3">
                <div className="flex gap-4 mb-3">
                  <input
                    type="text"
                    value={formatDate(internalStartDate)}
                    readOnly
                    placeholder="Start Date"
                    className="flex-1 px-2 py-1 border border-orange-400 rounded text-center text-gray-500 bg-white text-xs"
                  />
                  <input
                    type="text"
                    value={formatDate(internalEndDate)}
                    readOnly
                    placeholder="End Date"
                    className="flex-1 px-2 py-1 border rounded text-center text-gray-500 bg-white text-xs"
                  />
                </div>

                <div className="flex items-start gap-6 mb-3">
                  <button
                    onClick={handlePrevMonth}
                    className="mt-6 text-[#34656d] font-bold hover:text-[#34656d] p-1"
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <div className="flex gap-4 flex-1">
                    {renderCalendar(0)}
                    {renderCalendar(1)}
                  </div>

                  <button
                    onClick={handleNextMonth}
                    className="mt-6 text-gray-600 hover:text-gray-800 p-1"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>

                <div className="flex justify-center gap-2">
                  <button
                    onClick={handleClear}
                    className="px-6 py-1.5 rounded-lg font-medium transition-colors border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs"
                  >
                    Clear
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="px-6 py-1.5 rounded-lg font-medium transition-colors bg-[#F1935C] text-white hover:bg-[#e0844b] text-xs"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeSelector;
